import { useCallback, useEffect, useMemo, useState } from 'react'

import { isIOSDevice, isRunningStandalone } from '@/utils/pwa'

import { useMediaQuery } from './useMediaQuery'

/** `beforeinstallprompt` não é tipado no lib DOM padrão. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** Enquanto durar a aba: o usuário fechou o aviso e não quer vê-lo de novo agora. */
const DISMISS_KEY = 'pc-install-dismissed'

export type InstallPromptMode = 'native' | 'ios'

interface UsePwaInstall {
  /** `true` quando faz sentido oferecer a instalação (rota ainda é responsabilidade do componente). */
  visible: boolean
  /** `native`: navegador tem API de instalação. `ios`: só instruções manuais (Safari). */
  mode: InstallPromptMode
  /** Dispara o diálogo nativo do navegador. No-op fora do modo `native`. */
  promptInstall: () => Promise<void>
  /** Esconde o aviso até a aba ser fechada. */
  dismiss: () => void
}

function readDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Concentra a lógica de "instalar como app": captura o `beforeinstallprompt`,
 * detecta app já instalado e o caso do iOS (sem API). Só deve ser montado uma vez.
 */
export function usePwaInstall(): UsePwaInstall {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [installed, setInstalled] = useState(() => isRunningStandalone())
  const [dismissed, setDismissed] = useState(readDismissed)

  const standalone = useMediaQuery('(display-mode: standalone)')
  const isIOS = useMemo(() => isIOSDevice(), [])

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      // Impede o mini-infobar padrão do Chrome; a instalação passa a ser via nosso card.
      event.preventDefault()
      setDeferredPrompt(event as BeforeInstallPromptEvent)
    }
    const onAppInstalled = () => {
      setDeferredPrompt(null)
      setInstalled(true)
    }

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [])

  const dismiss = useCallback(() => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1')
    } catch {
      /* sessionStorage indisponível — dispensa vale só para esta montagem */
    }
    setDismissed(true)
  }, [])

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return
    await deferredPrompt.prompt()
    await deferredPrompt.userChoice
    setDeferredPrompt(null)
  }, [deferredPrompt])

  const isInstalled = installed || standalone
  const mode: InstallPromptMode = deferredPrompt ? 'native' : 'ios'
  const visible = !isInstalled && !dismissed && (deferredPrompt !== null || isIOS)

  return { visible, mode, promptInstall, dismiss }
}
